/**
 * Page-level smooth scroll (Lenis), matched to the safe defaults used by
 * premium Shopify themes like Radian:
 *  - lerp 0.1 by default, smoothWheel for mouse/trackpad, syncTouch for
 *    real touch devices — smooth scroll now runs on desktop AND mobile.
 *  - Fully skipped under prefers-reduced-motion.
 *  - Stops/starts in sync with Horizon's own scroll lock (the
 *    `scroll-lock` attribute Horizon's dialog/drawer components set on
 *    <html>), so it never fights the cart drawer, search modal, or mobile
 *    menu.
 *  - Off entirely unless the merchant enables it (theme setting), same as
 *    Horizon's other motion toggles.
 *
 * The Lenis vendor library itself is only fetched when actually needed —
 * skipped completely when smooth scroll can't run (disabled, reduced
 * motion, or the merchant's narrow admin preview — see below).
 *
 * WHICH ELEMENT ACTUALLY SCROLLS, AND WHY THIS IS DETECTED AT RUNTIME:
 * Horizon's `.page-wrapper` (layout/theme.liquid) is the real scrolling
 * element on wide/desktop layouts (`overflow-y: auto`, sized to the
 * viewport) — `window`/`<html>` has zero scroll range there, so Lenis
 * must be told explicitly to use `.page-wrapper`. But at narrow/mobile
 * widths, Horizon's own CSS switches `.page-wrapper` back to normal flow
 * (`overflow: visible`) and the document itself scrolls instead — forcing
 * `.page-wrapper` as the wrapper there would reproduce the exact "zero
 * scroll range" bug this was built to fix, just on mobile. So the wrapper
 * is resolved at Lenis-construction time by checking `.page-wrapper`'s
 * actual computed `overflow-y`, instead of assuming one element or
 * guessing from viewport width: `auto`/`scroll`/`overlay` means
 * `.page-wrapper` is real and gets passed to Lenis; anything else means
 * Lenis is left on its own default (`window`/`document.documentElement`),
 * which is the correct, standard setup for normal document scrolling.
 *
 * The merchant's Shopify admin mobile-preview toggle is a narrowed
 * iframe on their real desktop browser — it still reports a mouse
 * (hover: hover, pointer: fine), unlike an actual phone. Smooth scroll is
 * skipped there specifically (narrow width + no touch capability) so a
 * resize timing hiccup in that preview can never affect a real visitor;
 * an actual touch phone at the same width is never caught by this,
 * because it always reports pointer: coarse.
 *
 * SAFETY NET: shortly after Lenis starts, and again once the page has
 * fully loaded, its measured scroll range is sanity-checked against the
 * real content height. If they're wildly out of sync (a sign the
 * measurement was taken before layout settled), Lenis is torn down and
 * native scrolling takes back over rather than leaving anyone stuck —
 * this project has twice shipped a Lenis change that froze scrolling
 * outright, so this check is deliberately paranoid.
 *
 * The "Scroll sensitivity" theme setting (config/settings_schema.json,
 * settings.smooth_scroll_sensitivity, a 1-10 range) controls how quickly
 * the scroll position catches up to input — Lenis's `lerp` for
 * mouse/trackpad and `syncTouchLerp` for touch. Lower feels
 * slower/floatier, higher feels snappier/more direct. It's read from
 * `data-lenis-sensitivity` on <body> (see layout/theme.liquid) so
 * merchants can tune the feel from the theme editor without a code edit.
 * Default (5) preserves the lerp: 0.1 this was hardcoded to before.
 */
(function () {
  'use strict';

  var INSTANCE_PROP = '__luxLenisController';
  var MOBILE_MAX_WIDTH = '(max-width: 749px)';
  var DEFAULT_SENSITIVITY = 5;
  var SENSITIVITY_TO_LERP = 0.02; // sensitivity 5 (default) -> lerp 0.1, unchanged from before this setting existed
  var SANITY_CHECK_DELAY_MS = 400;
  var MIN_UNEXPLAINED_SCROLL_GAP = 150; // px of real, unaccounted-for scroll range before we distrust the measurement
  var MAX_SELF_HEAL_ATTEMPTS = 2; // resolveScrollWrapper() can read a transiently-wrong overflow right at construction, before layout/style has fully settled; a mismatch re-resolves and rebuilds instead of just giving up

  var selfHealAttempts = 0;

  var reducedMotionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var coarsePointerQuery = window.matchMedia ? window.matchMedia('(hover: none) and (pointer: coarse)') : null;
  var mobileWidthQuery = window.matchMedia ? window.matchMedia(MOBILE_MAX_WIDTH) : null;

  function prefersReducedMotion() {
    return !!(reducedMotionQuery && reducedMotionQuery.matches);
  }

  function isTouchPrimary() {
    return !!(coarsePointerQuery && coarsePointerQuery.matches);
  }

  function isMobileWidth() {
    return !!(mobileWidthQuery && mobileWidthQuery.matches);
  }

  // Only the merchant's own narrow-but-mouse-driven admin preview is
  // excluded. A real phone is always touch-primary, so it's never caught
  // by this — smooth scroll runs for it like any other visitor.
  function isNonTouchNarrowPreview() {
    return isMobileWidth() && !isTouchPrimary();
  }

  function isEnabled() {
    return !!(document.body && document.body.getAttribute('data-lenis-enabled') === 'true');
  }

  function getSensitivity() {
    var raw = document.body && document.body.getAttribute('data-lenis-sensitivity');
    var value = parseFloat(raw);
    if (!isFinite(value) || value < 1 || value > 10) return DEFAULT_SENSITIVITY;
    return value;
  }

  function getLerp() {
    return getSensitivity() * SENSITIVITY_TO_LERP;
  }

  function canUseLenis() {
    return isEnabled() && !prefersReducedMotion() && !isNonTouchNarrowPreview();
  }

  function isScrollLocked() {
    return document.documentElement.hasAttribute('scroll-lock');
  }

  var lockObserver = null;

  function syncScrollLock(controller) {
    if (!controller || !controller.lenis) return;
    if (isScrollLocked()) {
      controller.lenis.stop();
    } else {
      controller.lenis.start();
    }
  }

  function observeScrollLock(controller) {
    if (typeof MutationObserver !== 'function') return;
    if (lockObserver) lockObserver.disconnect();
    lockObserver = new MutationObserver(function () {
      syncScrollLock(controller);
    });
    lockObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['scroll-lock'] });
    syncScrollLock(controller);
  }

  var vendorLoadPromise = null;

  function loadVendor() {
    if (typeof window.Lenis === 'function') return Promise.resolve();
    if (vendorLoadPromise) return vendorLoadPromise;

    var src = document.body && document.body.dataset.lenisVendorScript;
    if (!src) return Promise.resolve();

    vendorLoadPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    return vendorLoadPromise;
  }

  // Picks which element Lenis should treat as the scroll container, based
  // on how it's actually laid out right now rather than assuming or
  // guessing from viewport width. Returns null to mean "let Lenis use its
  // own default (window / document.documentElement)".
  function resolveScrollWrapper() {
    var wrapper = document.querySelector('.page-wrapper');
    if (!wrapper) return null;

    var overflowY = window.getComputedStyle(wrapper).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
      return wrapper;
    }
    return null;
  }

  function getRealScrollRange(scrollWrapper) {
    var el = scrollWrapper || document.scrollingElement || document.documentElement;
    return el.scrollHeight - el.clientHeight;
  }

  function sanityCheck(controller, scrollWrapper) {
    if (!controller || window[INSTANCE_PROP] !== controller) return; // already torn down
    var realExtra = getRealScrollRange(scrollWrapper);
    if (realExtra <= MIN_UNEXPLAINED_SCROLL_GAP || controller.lenis.limit >= realExtra * 0.5) {
      selfHealAttempts = 0; // measurement looks right; give a full retry budget to any future problem
      return;
    }

    // Lenis is measuring far less scroll range than the page actually has —
    // almost always because resolveScrollWrapper() read `.page-wrapper`'s
    // overflow before the browser had finished applying styles for this
    // layout, and picked the wrong element to control. Rebuild once or
    // twice with a fresh read (styles are certainly settled by now) rather
    // than accepting a scroll experience that's stuck or badly wrong.
    // Only after repeated failures do we give up and fall back to native
    // scrolling — the exact failure mode that broke live twice before, so
    // that fallback still exists as the final safety net.
    destroyLenis();
    if (selfHealAttempts < MAX_SELF_HEAL_ATTEMPTS && canUseLenis()) {
      selfHealAttempts++;
      createLenis();
    }
  }

  function createLenis() {
    if (!canUseLenis() || typeof window.Lenis !== 'function') return null;
    if (window[INSTANCE_PROP]) return window[INSTANCE_PROP];

    var scrollWrapper = resolveScrollWrapper();
    var touchPrimary = isTouchPrimary();
    var lerp = getLerp();

    var lenisOptions = {
      lerp: lerp,
      smoothWheel: true,
      syncTouch: touchPrimary,
      syncTouchLerp: lerp,
    };
    if (scrollWrapper) {
      lenisOptions.wrapper = scrollWrapper;
      lenisOptions.content = scrollWrapper;
    }

    var lenis;
    try {
      lenis = new window.Lenis(lenisOptions);
    } catch (err) {
      // Never leave scrolling in a bad state if construction itself fails.
      return null;
    }

    var controller = { lenis: lenis, rafId: 0 };

    function raf(time) {
      try {
        lenis.raf(time);
      } catch (err) {
        destroyLenis();
        return;
      }
      controller.rafId = window.requestAnimationFrame(raf);
    }
    controller.rafId = window.requestAnimationFrame(raf);

    window[INSTANCE_PROP] = controller;
    observeScrollLock(controller);

    window.setTimeout(function () {
      sanityCheck(controller, scrollWrapper);
    }, SANITY_CHECK_DELAY_MS);
    window.addEventListener(
      'load',
      function () {
        window.setTimeout(function () {
          sanityCheck(controller, scrollWrapper);
        }, SANITY_CHECK_DELAY_MS);
      },
      { once: true }
    );

    return controller;
  }

  function destroyLenis() {
    var controller = window[INSTANCE_PROP];
    if (!controller) return;

    if (controller.rafId) window.cancelAnimationFrame(controller.rafId);
    if (lockObserver) {
      lockObserver.disconnect();
      lockObserver = null;
    }
    controller.lenis.destroy();
    window[INSTANCE_PROP] = null;
  }

  function sync() {
    if (canUseLenis()) {
      if (typeof window.Lenis === 'function') {
        createLenis();
      } else {
        loadVendor()
          .then(function () {
            if (canUseLenis()) createLenis();
          })
          .catch(function () {
            vendorLoadPromise = null;
          });
      }
    } else {
      destroyLenis();
    }
  }

  function init() {
    // Wait a couple of frames before the very first attempt so
    // `.page-wrapper`'s layout/styles have fully settled — reading its
    // computed overflow immediately at parse time can catch a transient,
    // not-yet-applied value (see the self-healing note in sanityCheck()).
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(sync);
    });
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  if (reducedMotionQuery) reducedMotionQuery.addEventListener('change', sync);
  if (coarsePointerQuery) coarsePointerQuery.addEventListener('change', sync);
  if (mobileWidthQuery) mobileWidthQuery.addEventListener('change', sync);

  // Layout can change height when a section is swapped in the theme
  // editor (or via the Section Rendering API) — let Lenis re-measure and
  // re-run the sanity check against the fresh layout.
  document.addEventListener('shopify:section:load', function () {
    var controller = window[INSTANCE_PROP];
    if (controller && controller.lenis && typeof controller.lenis.resize === 'function') {
      window.requestAnimationFrame(function () {
        controller.lenis.resize();
        window.setTimeout(function () {
          sanityCheck(controller, resolveScrollWrapper());
        }, SANITY_CHECK_DELAY_MS);
      });
    }
  });
})();
